import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, DialogTitle, DialogActions, DialogContent, Snackbar, Alert, CircularProgress } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { api } from '../../../redux/api';

function ReloadTemplate({ open, onClose, onConfirm, contextMenuNode, selectedNodes }) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { account } = useSelector(state => state.queryParams);
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
      });
    
      const handleCancelClick = () => {
        onClose();
    };
    
    const modifyTemplate = (templatesList, type, processedUnsavedTemplates, savedTemplates) => {
        const typeMap = {
            'inbound': 'inbound_templates',
            'outbound': 'outbound_templates',
            'workflow': 'workflows'
        }
        for (let i = 0; i < templatesList?.length; i++) {
            const obj = templatesList[i];
            let isMatched = false;
            
            for(let j = 0; j < selectedNodes.length; j++) {
                const selectedNode = selectedNodes[j];
                if (obj.template === (selectedNode.name || selectedNode.title) && obj.workflow_group === selectedNode.workflowGroup) {
                    const temp = {
                        ...obj,
                        template_type: type,
                        path: `${obj.account}/${obj.workflow_group}/${obj.template}`
                    };
                    processedUnsavedTemplates?.push(temp);
                    isMatched = true;
                    break; 
                }
                
                // Check for template_folder match
                if (selectedNode.type === 'template_folder' && 
                    obj.workflow_group === selectedNode.workflowGroup &&
                    typeMap[type] === (selectedNode.title || selectedNode.name)) {
                    const temp = {
                        ...obj,
                        template_type: type,
                        path: `${obj.account}/${obj.workflow_group}/${obj.template}`
                    };
                    processedUnsavedTemplates?.push(temp);
                    isMatched = true;
                }
            }
            
            if (!isMatched) {
                const save = {
                    ...obj,
                    template_type: type,
                    path: `${obj.account}/${obj.workflow_group}/${obj.template}`
                };
                savedTemplates?.push(save);
            }
        }
    }
    
    const handleReload = (processedUnsavedTemplates, savedTemplates) => {
        const getProps = (o) => {
            return { account: o.account, workflow_group: o.workflow_group, template: o.template }
        }
        const revertData = {
            inbound_templates: processedUnsavedTemplates?.filter(o => o.template_type === 'inbound').map(getProps),
            outbound_templates: processedUnsavedTemplates?.filter(o => o.template_type === 'outbound').map(getProps),
            workflow: processedUnsavedTemplates?.filter(o => o.template_type === 'workflow').map(getProps),
        }
        dispatch(api.endpoints.revertAllTemplates.initiate(
            { data: revertData },
            { forceRefetch: true }
        )).then((res) => {
            if (res.data?.current_state === "SUCCESS") {
                setSnackbar({
                    open: true,
                    message: t('LBL_RELOAD_SUCCESS'),
                    severity: "success",
                  });
            } else {
                console.warn('Revert request failure: ', res);
                setSnackbar({
                    open: true,
                    message: t('LBL_RELOAD_FAILURE'),
                    severity: "error",
                  });
            }
            dispatch(api.endpoints.getUnsavedTemplates.initiate(
                { account }, { forceRefetch: true }
              ));
            setLoading(false);
        }).catch((error) => {
            console.error('Reload template error:', error);
            setSnackbar({
                open: true,
                message: t('LBL_RELOAD_FAILURE'),
                severity: "error",
              });
            setLoading(false);
        });
        onClose();
    }
    
    const processTemplates = (templates) => {
        let processedUnsavedTemplates = [];
        let savedTemplates = [];
        if (templates.current_state === 'SUCCESS' && templates.data) {
            modifyTemplate(templates.data.inbound_templates, 'inbound', processedUnsavedTemplates, savedTemplates);
            modifyTemplate(templates.data.outbound_templates, 'outbound', processedUnsavedTemplates, savedTemplates);
            modifyTemplate(templates.data.workflow, 'workflow', processedUnsavedTemplates, savedTemplates);
        }
        handleReload(processedUnsavedTemplates, savedTemplates)
    }

    const getTemplateList = () => {
        dispatch(api.endpoints.getUnsavedTemplates.initiate(
            { account }, { forceRefetch: true }
        )).then(response => {
            processTemplates(response.data);
            if (!response.data){
                console.warn('getUnsavedTemplates no data:', response);
                setLoading(false);
            } 
        }).catch((error) => {
            console.error('Get template list error:', error);
            setSnackbar({
                open: true,
                message: t('LBL_RELOAD_FAILURE'),
                severity: "error",
              });
            setLoading(false);
        });
    }

    const handleReloadClick = () => {
        setLoading(true);
        getTemplateList();
    };

    return (
        <>
        <Dialog open={open} maxWidth='xs' onClose={onClose}>
            <DialogTitle className='dialog-header'>
                <div className='title'>{t('BOX_CONFIRM')}</div>
            </DialogTitle>
            <DialogContent className='modal-content' dividers>
                <div>
                    {t('BOX_ARE_YOU_SURE_YOU_WANT_TO_RELOAD_')} <br />
                    <br />
                    <span style={{fontWeight: 'bold'}}>
                        {selectedNodes?.map((node, index) => (
                            <div key={index}>
                                {node.type === 'template_folder' 
                                    ? `${node.workflowGroup}/${node.title || node.name}`
                                    : (node.name || node.title)
                                }
                            </div>
                        ))}
                    </span> <br />
                    <br />
                </div>
            </DialogContent>
            <DialogActions>
                <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    onClick={handleReloadClick}
                    disabled={loading}
                > 
                    {loading ? <CircularProgress size={"20px"}/> : t('BTN_OK')}
                </Button>
                <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    onClick={handleCancelClick}
                > 
                    {t('CANCEL')}
                </Button>
            </DialogActions>
        </Dialog>
           <Snackbar
           open={snackbar.open}
           autoHideDuration={6000}
           onClose={() => setSnackbar({ ...snackbar, open: false })}>
           <Alert
             onClose={() => setSnackbar({ ...snackbar, open: false })}
             severity={snackbar.severity}
             variant="filled"
             sx={{
               width: "100%",
               maxWidth: "450px",
               whiteSpace: "normal",
               wordBreak: "break-word",
             }}>
             {snackbar.message}
           </Alert>
         </Snackbar>
         </>
    );
}

export default ReloadTemplate; 